import { featureFlags } from "$global-providers/feature-flags-provider/feature-flags";
import { useFeatureFlag } from "$global-providers/feature-flags-provider/provider";
import { useLazyUser } from "$global-providers/user-provider";
import { useQuerystring } from "$routes/_lib/use-querystring";
import { gql } from "@apollo/client";
import type {
  ImagingStudySyncPushInput,
  QueryPullImagingStudyArgs,
} from "elephant-web/gql";
import { useMemo } from "react";
import { useMatch } from "react-router-dom";
import type { RxCollection, RxDatabase, WithDeleted } from "rxdb";
import type { ImagingStudy as RxImagingStudy } from "../collections/imaging-study";
import type { RxDBCollections } from "../provider";
import {
  ImagingStudyPullDocument,
  SyncImagingStudyPushDocument,
  type WholeSyncableImagingStudyFragment,
} from "./__generated__/imaging-study.gql";
import { createPersistedCheckpointPullBuilders } from "./create-persisted-checkpoint-pull-builders";
import {
  IMAGING_REPLICATION_ROLES,
  isReplicationRoleAllowed,
} from "./replication-role-allowlist";
import {
  type ReplicationPullConfig,
  useGqlReplication,
} from "./use-gql-replication";

export const useReplicateImagingStudy = ({
  db,
  collection,
}: {
  db?: RxDatabase<RxDBCollections>;
  collection?: RxCollection<RxImagingStudy>;
}) => {
  const query = useQuerystring();
  const matchPat = useMatch("/dev-patient/:patientId/*");
  const elephantPatientId: string | undefined | null =
    matchPat?.params.patientId ?? query.patientUuid;
  const [featureFlagEnabled] = useFeatureFlag(
    featureFlags.pocus.imagingStudySync,
  );
  const role = useLazyUser()?.role;
  const active =
    featureFlagEnabled &&
    isReplicationRoleAllowed(IMAGING_REPLICATION_ROLES, role);

  const push = useMemo(
    () => ({
      mutation: SyncImagingStudyPushDocument,
      mapRxToGql: mapRxImagingStudyToGqlImagingStudyInput,
    }),
    [],
  );

  const pull = useMemo<
    ReplicationPullConfig<WholeSyncableImagingStudyFragment>
  >(() => {
    return {
      query: ImagingStudyPullDocument,
      reSyncInterval: { minutes: 30 },
      ...createPersistedCheckpointPullBuilders<
        QueryPullImagingStudyArgs,
        WholeSyncableImagingStudyFragment
      >({
        buildInputVariables: (checkpoint, batchSize) => ({
          input: {
            limit: batchSize,
            filters: { elephantPatientId },
            checkpoint,
          },
        }),
        buildPullCheckpoint: (doc) => ({
          id: doc.id,
          updatedAt: doc.meta.updatedAt,
        }),
        entityId: elephantPatientId,
        db,
        collectionName: collection?.name,
      }),
    };
  }, [elephantPatientId, db, collection]);

  const replicationResult = useGqlReplication<
    RxImagingStudy,
    WholeSyncableImagingStudyFragment,
    ImagingStudySyncPushInput
  >({
    db,
    collection,
    push: active && elephantPatientId ? push : undefined,
    pull: active && elephantPatientId ? pull : undefined,
    mapGqlToRx: mapGqlImagingStudyToRxImagingStudy,
    batchSize: 50,
    expireData: true,
    metadataLocalStorageKeySuffix: `imaging-study-${elephantPatientId}`,
    enabled: active && !!elephantPatientId,
  });

  return replicationResult;
};

export const mapGqlImagingStudyToRxImagingStudy = (
  imagingStudy: WholeSyncableImagingStudyFragment,
): WithDeleted<RxImagingStudy> => ({
  id: imagingStudy.id,
  elephantPatientId: imagingStudy.elephantPatientId,
  // temporary - need backend PR first to get the new types
  accessionNumber: imagingStudy.accessionNumber,
  meta: {
    createdAt: imagingStudy.meta.createdAt,
    createdByUserId: imagingStudy.meta.createdByUserId,
    updatedAt: imagingStudy.meta.updatedAt,
    updatedByUserId: imagingStudy.meta.updatedByUserId,
  },
  _deleted: imagingStudy.meta.deleted,
});

export const mapRxImagingStudyToGqlImagingStudyInput = (
  imagingStudy: WithDeleted<RxImagingStudy>,
): ImagingStudySyncPushInput => ({
  id: imagingStudy.id,
  elephantPatientId: imagingStudy.elephantPatientId,
  accessionNumber: imagingStudy.accessionNumber,
  meta: {
    createdAt: imagingStudy.meta.createdAt,
    createdByUserId: imagingStudy.meta.createdByUserId,
    updatedAt: imagingStudy.meta.updatedAt,
    updatedByUserId: imagingStudy.meta.updatedByUserId,
    deleted: imagingStudy._deleted,
  },
});

gql`
  fragment WholeSyncableImagingStudy on SyncableImagingStudy {
    id
    accessionNumber
    elephantPatientId
    meta {
      createdAt
      updatedAt
      createdByUserId
      updatedByUserId
      deleted
    }
  }
`;

gql`
  mutation SyncImagingStudyPush($input: PushImagingStudyInput!) {
    pushImagingStudy(input: $input) {
      rows {
        ...WholeSyncableImagingStudy
      }
    }
  }
`;

gql`
  query ImagingStudyPull($input: PullImagingStudyInput!) {
    pullImagingStudy(input: $input) {
      documents {
        ...WholeSyncableImagingStudy
      }
    }
  }
`;
